import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import { NSButton, NSCard, NSLabel, NSSelect } from 'bricks';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import './OperatingInformation.scss';
import { IOperatingAndPermanentDebtInfo, IPatchOperatingInfoData } from 'views/ProFormaTable/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds, IncomeCalculationType, DisplayMetricsType, MONTH_OPTIONS} from 'constants/proForma';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import {
    LEASE_UP_PACE_TOOLTIP,
    OCCUPANCY_UPON_STABILIZATION, PRO_FORMA_TABLE_NAV, PRO_FORMA_TABS, ProjectActualsDiscrepanciesEnum,
} from 'views/ProFormaTable/constants';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { IOptionAnyValue } from 'bricks/types';
import MonthlyAnalysisTable, { IMonthlyAnalysisPeriod } from './MonthlyAnalysisTable/MonthlyAnalysisTable';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { getMonthlyAnalysisPeriods, getStabilizedMonthMissingEntity } from './helpers';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';

export interface IOperatingInformationProps {
    signUpMode?: boolean;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
}

const OperatingInformation = ({ signUpMode = false, navigateToSection }: IOperatingInformationProps) => {
    const {
        proForma, operatingInfo, operatingPeriodCashFlows, handleUpdateProFormaOperations,
        cashFlowPeriods, shouldUseProjectActuals, projectActuals, uses,
    } = useProFormaSocketContext();
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId);
    const isApartmentWithRetail = proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const isStudentHousing = proForma.proFormaModelId === ProFormaModelIds.STUDENT_HOUSING;

    const [operatingInfoData, setOperatingInfoData] = useState<Partial<IOperatingAndPermanentDebtInfo>>({});
    const [operationsStartYear, setOperationsStartYear] = useState<number>(
        operatingInfo.operationStartDate
            ? parseISO(operatingInfo.operationStartDate).getFullYear()
            : cashFlowPeriods.length > 0 ? parseISO(cashFlowPeriods[0]).getFullYear() : new Date().getFullYear()
    );
    const [academicYearStartMonth, setAcademicYearStartMonth] = useState<number | null>(
        operatingInfo.operationStartDate ? parseISO(operatingInfo.operationStartDate).getMonth() + 1 : null
    );
    const [displayMetrics, setDisplayMetrics] = useState<DisplayMetricsType>(DisplayMetricsType.PER_BED);

    const labelClassName = 'pl-0 col-4 font-weight-normal text-dark align-self-center';
    const labelClassNameNoWrap = 'pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap';

    const actualsDiscrepancies = projectActuals?.projectActuals?.discrepancies || [];

    const updateOperationsKeys = (keyValues: Partial<IPatchOperatingInfoData>) => {
        if (operatingInfo?.id) {
            handleUpdateProFormaOperations(operatingInfo.id, keyValues);
        }
    };

    const monthlyAnalysisPeriods: IMonthlyAnalysisPeriod[] = getMonthlyAnalysisPeriods(
        proForma, cashFlowPeriods, operatingInfo, operatingPeriodCashFlows, operatingInfo.saleMonth,
    );

    const redirectToDevelopmentTab = () => {
        navigateToSection(PRO_FORMA_TABLE_NAV.PRO_FORMA.name, PRO_FORMA_TABS.DEVELOPMENT.name);
    };

    const stabilizedMonthMissingEntity = getStabilizedMonthMissingEntity(proForma, isIncomeCalculationTypeEstimate);

    const developmentEndMonth = uses?.developmentEndMonth ?? 0;
    const saleMonth = operatingInfo.saleMonth ?? cashFlowPeriods.length;
    const operationStartPeriods = cashFlowPeriods.slice(developmentEndMonth + 1, saleMonth);

    const operationStartDateOptions = operationStartPeriods.map((periodDate, index) => ({
        label: `${format(parseISO(periodDate), 'MMM yyyy')} (Month ${developmentEndMonth + 1 + index})`,
        value: periodDate,
    }));

    const operationStartYearOptions = Array.from(
        new Set(operationStartPeriods.map(periodDate => parseISO(periodDate).getFullYear()))
    ).map(year => ({
        label: year.toString(),
        value: year
    }));

    const operationStartDatePlaceholder = operatingInfo?.operationStartDate && operatingInfo?.operationBeginMonth
        ? `${format(parseISO(operatingInfo.operationStartDate), 'MMM yyyy')} (Month ${operatingInfo.operationBeginMonth})` : '';

    const handleOperationStartDateSelect = (option: IOptionAnyValue) => {
        if (operatingInfo?.id) {
            handleUpdateProFormaOperations(operatingInfo.id, { operationStartDate: option.value });
        }
    };

    const handleAcademicOperationStartDateSelect = (yearOption?: IOptionAnyValue | null, monthOption?: IOptionAnyValue | null) => {
        if (operatingInfo?.id) {
            // Use provided values or fallback to state defaults
            const selectedYear = yearOption?.value ?? operationsStartYear;
            const selectedMonth = monthOption?.value ?? academicYearStartMonth;
            
            if (selectedYear !== null && selectedMonth !== null) {
                // converts month to 2-digit format with leading zero if necessary
                const month = String(selectedMonth).padStart(2, '0');
                const operationStartDate = `${selectedYear}-${month}-01`;

                handleUpdateProFormaOperations(operatingInfo.id, { operationStartDate });
            }
        }
    };
    
    useEffect(() => {
        if (operatingInfo) {
            setOperatingInfoData({
                occupancyUponStabilizationPercentage: operatingInfo.occupancyUponStabilizationPercentage,
                rentalGrowthPercentage: operatingInfo.rentalGrowthPercentage,
                percentPreLeased: operatingInfo.percentPreLeased,
                leaseUpPace: operatingInfo.effectiveLeaseUpPace,
                userPaceReachesStabilization: operatingInfo.userPaceReachesStabilization,
                lengthOfLease: operatingInfo.lengthOfLease,
                initialFreeRent: operatingInfo.initialFreeRent,
                stabilizedFreeRent: operatingInfo.stabilizedFreeRent,
            });
        }
    }, [operatingInfo, operatingPeriodCashFlows]);

    return (
        <Row className="mx-n1 px-3">
            <Col className="px-1 my-3" sm={12} md={6}>
                <RenderIf isTrue={!isStudentHousing}>
                    <div className="d-flex align-items-center">
                        <div className={`d-flex my-auto ${labelClassName}`}>
                            <NSLabel className="my-auto font-weight-normal text-dark mr-1">Occupancy upon stabilization</NSLabel>
                            <IconWithTooltip
                                name="occupancyUponStabilization"
                                icon={faInfoCircle}
                                tooltipText={OCCUPANCY_UPON_STABILIZATION}
                            />
                        </div>
                        <NSInputNumberStepper
                            percentage
                            name="occupancyUponStabilizationPercentage"
                            value={operatingInfoData.occupancyUponStabilizationPercentage || null}
                            onChange={(value: number) => updateOperationsKeys({
                                occupancyUponStabilizationPercentage: value,
                            })}
                            min={0}
                            step={1}
                            max={100}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                </RenderIf>
                <RenderIf isTrue={(!isApartment && !isStudentHousing) || isApartmentWithRetail}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={`my-auto ${labelClassName}`}>
                            {isApartmentWithRetail ? 'Retail income' : 'Income calculation'}
                        </NSLabel>
                        <div className="my-auto font-weight-normal text-dark text-nowrap">
                            {
                                signUpMode ? (
                                    <>
                                        <NSButtonBase
                                            color={isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.ESTIMATE })}
                                            text="Allocation"
                                            className={classNames('px-3', { 'border border-primary bg-primary-lighten': false })}
                                        />
                                        <NSButtonBase
                                            color={isIncomeCalculationTypeEstimate ? 'secondary' : 'primary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.TENANTS })}
                                            text="Rent roll"
                                            className={classNames('px-3', {
                                                'border border-primary bg-primary-lighten': false,
                                            })}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <NSButton
                                            color={isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.ESTIMATE })}
                                            text="Allocation"
                                            className={classNames('px-3', { 'border border-primary bg-primary-lighten': false })}
                                        />
                                        <NSButton
                                            color={!isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperationsKeys({ incomeCalculationType: IncomeCalculationType.TENANTS })}
                                            text="Rent roll"
                                            className={classNames('px-3', {
                                                'border border-primary bg-primary-lighten': false,
                                            })}
                                        />
                                    </>
                                )
                            }
                        </div>
                    </div>
                </RenderIf>
                <RenderIf
                    isTrue={isIncomeCalculationTypeEstimate || isApartment}
                >
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Rent growth (annual)</NSLabel>
                        <NSInputNumberStepper
                            percentage
                            name="rentalGrowthPercentage"
                            value={operatingInfoData.rentalGrowthPercentage || null}
                            onChange={(value: number) => updateOperationsKeys({ rentalGrowthPercentage: value })}
                            min={0}
                            step={0.1}
                            max={100}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                    <RenderIf isTrue={!isStudentHousing}>
                        <div className="d-flex align-items-center mt-2">
                            <NSLabel className={labelClassNameNoWrap}>% pre-leased</NSLabel>
                            <NSInputNumberStepper
                                percentage
                                name="percentPreLeased"
                                value={operatingInfoData.percentPreLeased || null}
                                onChange={(value: number) => updateOperationsKeys({ percentPreLeased: value })}
                                min={0}
                                step={1}
                                max={100}
                                inputGroupClassName="OperatingInformation__input-width"
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <div className={`d-flex my-auto ${labelClassName}`}>
                                <NSLabel className="my-auto font-weight-normal text-dark mr-1">
                                    Lease-up pace
                                    {` (${isApartment ? 'units' : 'SF'}/month)`}
                                </NSLabel>
                                <RenderIf
                                    isTrue={!operatingInfoData.userPaceReachesStabilization}
                                >
                                    <IconWithTooltip
                                        name="occupancyUponStabilization"
                                        icon={faInfoCircle}
                                        tooltipText={LEASE_UP_PACE_TOOLTIP}
                                    />
                                </RenderIf>
                            </div>
                            <NSInputNumberStepper
                                name="leaseUpPace"
                                value={operatingInfoData.leaseUpPace || null}
                                onChange={(value: number) => updateOperationsKeys({ leaseUpPace: value })}
                                min={0}
                                step={1}
                                inputGroupClassName="OperatingInformation__input-width"
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <NSLabel className={labelClassNameNoWrap}>Length of lease</NSLabel>
                            <NSInputNumberStepper
                                name="lengthOfLease"
                                value={operatingInfoData.lengthOfLease || null}
                                onChange={(value: number) => updateOperationsKeys({ lengthOfLease: value })}
                                min={1}
                                step={1}
                                customSuffix={(operatingInfo.lengthOfLease || 0) > 1 ? 'months' : 'month'}
                                inputGroupClassName="OperatingInformation__input-width"
                            />
                        </div>
                    </RenderIf>
                </RenderIf>
                <RenderIf isTrue={isStudentHousing}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Operations start year</NSLabel>
                        <NSSelect
                            name="operationsStartYear"
                            options={operationStartYearOptions}
                            value={operationStartYearOptions.find(option => {
                                if (!operatingInfo?.operationStartDate) return false;
                                const infoDate = parseISO(operatingInfo.operationStartDate);
                                return infoDate.getFullYear() === option.value;
                            }) || (operationsStartYear ? { label: operationsStartYear.toString(), value: operationsStartYear } : null)}
                            onChange={(option: IOptionAnyValue) => {
                                setOperationsStartYear(option?.value);
                                handleAcademicOperationStartDateSelect(option);
                            }}
                            placeholder="Select year"
                            isClearable={false}
                            menuPortalTarget={document.body}
                            formClassName="OperatingInformation__select-width"
                            styles={{
                                ...customStyles,
                                menuPortal: (provided: any) => ({ ...provided, zIndex: 10 }),
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Academic year start month</NSLabel>
                        <NSSelect
                            name="academicYearStartMonth"
                            options={MONTH_OPTIONS}
                            value={MONTH_OPTIONS.find(option => {
                                if (!operatingInfo?.operationStartDate) return academicYearStartMonth === option.value;
                                const infoDate = parseISO(operatingInfo.operationStartDate);
                                return infoDate.getMonth() + 1 === option.value; // Convert 0-based getMonth() to 1-based
                            }) || (academicYearStartMonth !== null
                                ? MONTH_OPTIONS.find(option => option.value === academicYearStartMonth) || null
                                : null)}
                            onChange={(option: IOptionAnyValue) => {
                                setAcademicYearStartMonth(option?.value);
                                handleAcademicOperationStartDateSelect(null, option);
                            }}
                            placeholder="Select month"
                            isClearable={false}
                            menuPortalTarget={document.body}
                            formClassName="OperatingInformation__select-width"
                            styles={{
                                ...customStyles,
                                menuPortal: (provided: any) => ({ ...provided, zIndex: 10 }),
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Display metrics</NSLabel>
                        <div className="my-auto font-weight-normal text-dark text-nowrap">
                            <NSButtonBase
                                color={displayMetrics === DisplayMetricsType.PER_BED ? 'primary' : 'secondary'}
                                callback={() => setDisplayMetrics(DisplayMetricsType.PER_BED)}
                                text="Per bed"
                                outline={false}
                                className={classNames('px-3', { 'border border-primary bg-primary-lighten': false })}
                            />
                            <NSButtonBase
                                color={displayMetrics === DisplayMetricsType.PER_UNIT ? 'primary' : 'secondary'}
                                callback={() => setDisplayMetrics(DisplayMetricsType.PER_UNIT)}
                                text="Per unit"
                                outline={false}
                                className={classNames('px-3', {
                                    'border border-primary bg-primary-lighten': false,
                                })}
                            />
                        </div>
                    </div>
                </RenderIf>
                <RenderIf isTrue={isApartment && !isStudentHousing}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Initial free rent</NSLabel>
                        <NSInputNumberStepper
                            name="initialFreeRent"
                            value={operatingInfoData.initialFreeRent || null}
                            onChange={(value: number) => updateOperationsKeys({ initialFreeRent: value })}
                            min={0}
                            step={1}
                            customSuffix={(operatingInfo.initialFreeRent || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassName}>Stabilized free rent</NSLabel>
                        <NSInputNumberStepper
                            name="stabilizedFreeRent"
                            value={operatingInfoData.stabilizedFreeRent || null}
                            onChange={(value: number) => updateOperationsKeys({ stabilizedFreeRent: value })}
                            min={0}
                            step={1}
                            customSuffix={(operatingInfoData.stabilizedFreeRent || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                </RenderIf>
            </Col>
            <Col className="px-1 my-3" sm={12} md={6}>
                <div className="d-flex">
                    <RenderIf isTrue={!!operatingInfo.operationStartDate && !isStudentHousing}>
                        <NSCard className="NSCard--level-3 h-100 p-2 flex-fill mr-2 w-50">
                            <span className="mb-0">Operation start</span>
                            <NSSelect
                                name="operationStartDate"
                                options={operationStartDateOptions}
                                value={operationStartDateOptions.find(option => {
                                    if (!operatingInfo?.operationStartDate) return false;
                                    const optDate = parseISO(option.value);
                                    const infoDate = parseISO(operatingInfo.operationStartDate);
                                    return optDate.getFullYear() === infoDate.getFullYear()
                                        && optDate.getMonth() === infoDate.getMonth();
                                })}
                                onChange={handleOperationStartDateSelect}
                                placeholder={operationStartDatePlaceholder}
                                isClearable={false}
                                isSearchable
                                menuPortalTarget={document.body}
                                formClassName="OperatingInformation__select-width"
                                styles={{
                                    ...customStyles,
                                    menuPortal: (provided: any) => ({ ...provided, zIndex: 10 }),
                                }}
                            />
                        </NSCard>
                    </RenderIf>
                    <RenderIf isTrue={!operatingInfo.operationStartDate && !isStudentHousing}>
                        <div className='flex-fill mr-2 w-50'>
                            <MetricOutputCard
                                label="Operation start"
                                value=""
                                showInProgress
                                showProjectActualsIcon={shouldUseProjectActuals
                                    && actualsDiscrepancies?.includes(ProjectActualsDiscrepanciesEnum.OPERATION_START_DATE)}
                                projectActualsIconName={ProjectActualsDiscrepanciesEnum.OPERATION_START_DATE}
                                inProgressText={(
                                    <span>
                                        In progress - provide start/end dates on
                                        {' '}
                                        <span
                                            role="presentation"
                                            onClick={redirectToDevelopmentTab}
                                            className="text-primary cursor--pointer"
                                        >
                                            Development: Uses
                                        </span>
                                        .
                                    </span>
                                )}
                            />
                        </div>
                    </RenderIf>
                    <RenderIf isTrue={isStudentHousing}>
                        <div className="flex-fill w-50 mr-2">
                            <MetricOutputCard
                                label="Lease term"
                                value="12 months"
                            />
                        </div>
                    </RenderIf>
                    <div className="flex-fill w-50">
                        <MetricOutputCard
                            label="1st stabilized month"
                            value={operatingInfo.stabilizedDate
                                ? `${format(parseISO(operatingInfo.stabilizedDate), 'MMM yyyy')} (Month ${operatingInfo.stabilizedMonth})` : ''}
                            showInProgress={!operatingInfo.stabilizedDate}
                            inProgressText={(
                                <span>
                                    In progress - provide operating information and
                                    {' '}
                                    {stabilizedMonthMissingEntity}
                                    .
                                </span>
                            )}
                        />
                    </div>
                </div>
                <MonthlyAnalysisTable
                    proFormaModelId={proForma.proFormaModelId}
                    monthlyAnalysisPeriods={monthlyAnalysisPeriods}
                    operationBeginMonth={operatingInfo.operationBeginMonth}
                />
            </Col>
        </Row>
    );
};

export default OperatingInformation;
